import { Injectable, inject } from '@angular/core';
import { Observable, combineLatest, map, of, take } from 'rxjs';

import { TenantService } from '@app/services/tenant.service';
import { UserService } from '@app/services/user.service';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import { PackageType } from '@shared/enums/package-type';
import { checkIfPackageAvailable, checkTrialDaysLeft } from '@shared/functions/checkPackages.function';
import { TenantData } from '@shared/interfaces/tenant-data';
import { UserData } from '@shared/interfaces/user-data';

export type PackageTrialState = {
  packageType: PackageType;
  trialState: number | undefined;
};

@Injectable({
  providedIn: 'root',
})
export class PackageService {
  private tenantService: TenantService = inject(TenantService);
  private timezoneService: TimezoneService = inject(TimezoneService);
  private userService: UserService = inject(UserService);

  checkPackages(tenantData: TenantData, packages: PackageType[], userData?: UserData): boolean {
    if (!tenantData) {
      return false;
    }

    if (!packages?.length) {
      return true;
    }

    return packages.some(p =>
      checkIfPackageAvailable(tenantData, p, this.timezoneService.getCurrentDateOnly(), userData)
    );
  }

  checkPackageAvailable(packageType: PackageType): Observable<boolean> {
    return this.tenantService.getCachedTenant().pipe(map(tenantData => this.checkPackages(tenantData, [packageType])));
  }

  checkPackagesAvailable(packages: PackageType[]): Observable<boolean> {
    return combineLatest([this.tenantService.getCachedTenant(), this.userService.getCashedUserData()]).pipe(
      map(([tenantData, userData]) => this.checkPackages(tenantData, packages, userData))
    );
  }

  checkPackageTrialDaysLeft(packageType: PackageType): Observable<PackageTrialState> {
    return this.tenantService.getCachedTenant().pipe(
      take(1),
      map(tenantData => ({
        packageType,
        trialState: checkTrialDaysLeft(tenantData, packageType, this.timezoneService.getCurrentDateOnly()),
      }))
    );
  }

  checkPackagesTrialDaysLeft(packages: PackageType[]): Observable<PackageTrialState[]> {
    if (!packages?.length) {
      return of([]);
    }

    return this.tenantService.getCachedTenant().pipe(
      take(1),
      map(tenantData => {
        return packages.map(packageType => ({
          packageType,
          trialState: checkTrialDaysLeft(tenantData, packageType, this.timezoneService.getCurrentDateOnly()),
        }));
      })
    );
  }
}
